import { Box, Flex } from "@chakra-ui/react"
import learnmachineDefaultBgVideo from "../../../assets/media/learnmachine.mp4"
import uttaDefaultBgVideo from "../../../assets/media/login-utta-bgvideo.mp4"
import useConfig from "src/hooks/useConfig"
import LearnMachineAuthScreen from "./LearnMachineAuthScreen"
import "roboto-fontface"
import DefaultAuthScreen from "./DefaultAuthScreen"
import { ReactNode } from "react"
import { useIsMobileLandscape } from "src/util/browser"
import UttaAuthScreen from "./UttaAuthScreen"
import usePageStyling from "src/hooks/usePageStyling"
import useWindowSize from "src/hooks/useWindowSize"
import useSearchParams from "src/hooks/useSearchParams"
import { AuthPageStyling } from "src/context/types"
import { useIsMobileApp } from "src/util/deviceType"
import X121AuthScreen from "./X121AuthScreen"
import { ConditionalAuthVideo } from "./ConditionalAuthVideo"

function AuthenticationLayoutContainer({ children }: { children: ReactNode }) {
  const config = useConfig()
  const roomStyles = usePageStyling("authPageStyling")
  const isMobileLandscape = useIsMobileLandscape()
  const isApp = useIsMobileApp()

  const showCustom =
    config.learnmachineAuthPage === true || config.uttaAuthPage === true

  return (
    <Flex
      align="center"
      justify="center"
      bgGradient="linear(to-r, orange.400,green.200)"
      backgroundColor={
        roomStyles?.authPageBackgroundColor
          ? roomStyles.authPageBackgroundColor
          : "gray.100"
      }
      bgImage={
        config.learnmachineAuthPage !== "loading" &&
        !config.learnmachineAuthPage &&
        roomStyles?.authPageBackgroundImage
          ? `url(${roomStyles.authPageBackgroundImage})`
          : "none"
      }
      bgSize="cover"
      backgroundPosition="center"
      bgRepeat="no-repeat"
      left="0"
      top="64px"
      w="100%"
      h="100%"
    >
      {showCustom && !isMobileLandscape && (
        <Box position="absolute" left={0} top={0} width="100%" height="100%">
          {/* #todo: dynamic video source */}
          <ConditionalAuthVideo />
        </Box>
      )}
      <AuthScreen
        isMobileLandscape={isMobileLandscape}
        isApp={isApp}
        config={config}
        roomStyles={roomStyles}
      >
        {children}
      </AuthScreen>
    </Flex>
  )
}

const AuthScreen = ({
  isMobileLandscape,
  isApp,
  config,
  roomStyles,
  children
}: {
  isMobileLandscape: boolean
  isApp: boolean
  config: ReturnType<typeof useConfig>
  roomStyles: AuthPageStyling
  children: ReactNode
}) => {
  const { platformName } = useConfig()
  const { screenSize } = useWindowSize()
  const isMobileApp = useIsMobileApp()
  const { isSecureInAppBrowser } = useSearchParams<{
    isSecureInAppBrowser?: true | undefined
  }>()
  const hideBackButton = !(
    isMobileApp &&
    !isSecureInAppBrowser &&
    screenSize.width > 430 &&
    screenSize.height > 730
  )

  switch (platformName) {
    case "LiveSell":
    case "LearnMachine":
      return (
        <LearnMachineAuthScreen
          isMobileLandscape={isMobileLandscape}
          isApp={isApp}
          hideBackButton={hideBackButton}
        >
          {children}
        </LearnMachineAuthScreen>
      )
    case "Utta":
      return (
        <UttaAuthScreen
          isMobileLandscape={isMobileLandscape}
          isApp={isApp}
          roomStyles={roomStyles}
          hideBackButton={hideBackButton}
        >
          {children}
        </UttaAuthScreen>
      )
    case "x121":
      return (
        <X121AuthScreen
          isMobileLandscape={isMobileLandscape}
          isApp={isApp}
          roomStyles={roomStyles}
          hideBackButton={hideBackButton}
        >
          {children}
        </X121AuthScreen>
      )
    case "loading":
    default:
      return (
        <DefaultAuthScreen
          isMobileLandscape={isMobileLandscape}
          roomStyles={roomStyles}
          hideBackButton={hideBackButton}
        >
          {children}
        </DefaultAuthScreen>
      )
  }
}

export default AuthenticationLayoutContainer
